import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import ElectricFenceChecklistFormState from "../../../types/forms/Permits/Conditions/ElectricFenceChecklistFormState";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import BaseUserInfo from "../../../types/BaseUserInfo";
import { SignatureManager } from "../../../hooks/useSignature";
import { CounterSignatureRequestInputManager } from "../../../hooks/useCounterSignatureRequestInput";
import CounterSignatureRequestInput from "../Permits/CounterSignatureRequestInput";

type ElectricFenceChecklistModalProps = {
    checklistVersion: number;
    show: boolean;
    onHide: () => void;
    resources: Resources;
    onChange: (fn: (prevValue: ElectricFenceChecklistFormState) => ElectricFenceChecklistFormState) => void;
    formState: ElectricFenceChecklistFormState;
    baseUserInfo: BaseUserInfo | undefined;
    canManageContent: boolean;

    powerOffDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOnPortOfficerSignatureManager?: SignatureManager;
    powerOffExternalDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOffResponsibleElectricianSignatureManager?: SignatureManager;
    powerOffWorkLeaderSignatureManager?: SignatureManager;
    powerOnDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOffPortOfficerSignatureManager?: SignatureManager;
    powerOnExternalDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOnResponsibleElectricianSignatureManager?: SignatureManager;
    powerOnWorkLeaderSignatureManager?: SignatureManager;

    powerOffCounterSignatureRequestHandler?: CounterSignatureRequestInputManager;
    powerOnCounterSignatureRequestHandler?: CounterSignatureRequestInputManager;
};

function ElectricFenceChecklistModal({
    checklistVersion,
    baseUserInfo,
    formState,
    onChange,
    onHide,
    resources,
    powerOffDirectorFlammableProductSignatureManager,
    powerOffExternalDirectorFlammableProductSignatureManager,
    powerOffResponsibleElectricianSignatureManager,
    powerOffWorkLeaderSignatureManager,
    powerOnDirectorFlammableProductSignatureManager,
    powerOnExternalDirectorFlammableProductSignatureManager,
    powerOnResponsibleElectricianSignatureManager,
    powerOnWorkLeaderSignatureManager,
    powerOffPortOfficerSignatureManager,
    powerOnPortOfficerSignatureManager,
    show,
    powerOffCounterSignatureRequestHandler,
    powerOnCounterSignatureRequestHandler,
    canManageContent
}: ElectricFenceChecklistModalProps) {
    function changeHandler(key: keyof ElectricFenceChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: ElectricFenceChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    const showPowerOffSignatures =
        !!baseUserInfo &&
        !!powerOffDirectorFlammableProductSignatureManager &&
        !!powerOffExternalDirectorFlammableProductSignatureManager &&
        !!powerOffResponsibleElectricianSignatureManager &&
        !!powerOffWorkLeaderSignatureManager &&
        !!powerOffCounterSignatureRequestHandler &&
        !!powerOffPortOfficerSignatureManager;

    const showPowerOnSignatures =
        !!baseUserInfo &&
        !!powerOnDirectorFlammableProductSignatureManager &&
        !!powerOnExternalDirectorFlammableProductSignatureManager &&
        !!powerOnResponsibleElectricianSignatureManager &&
        !!powerOnWorkLeaderSignatureManager &&
        !!powerOnCounterSignatureRequestHandler &&
        !!powerOnPortOfficerSignatureManager;

    const showPowerOffCounterSignatureRequestSection =
        powerOffCounterSignatureRequestHandler &&
        powerOffCounterSignatureRequestHandler.showCounterSignatureRequestSection;

    const showPowerOnCounterSignatureRequestSection =
        powerOnCounterSignatureRequestHandler &&
        powerOnCounterSignatureRequestHandler.showCounterSignatureRequestSection;

    const showPowerOffExternalDirectorFlammableProductSignature =
        powerOffCounterSignatureRequestHandler &&
        powerOffCounterSignatureRequestHandler.hasActiveCounterSignatureRequest;
    const showPowerOnExternalDirectorFlammableProductSignature =
        powerOnCounterSignatureRequestHandler && powerOnCounterSignatureRequestHandler.hasActiveCounterSignatureRequest;

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListForElectricFence")}</h1>
            </Modal.Header>
            <Modal.Body>
                <h6 className="fw-bold">{resources.get("DisconnectionOfElectricFence")}</h6>
                <p>{resources.get("IsTheFollowingDone")}</p>
                {checklistVersion === 0 ? (
                    <PowerOffVersion0
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                    />
                ) : (
                    <PowerOffVersion2026_02
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                    />
                )}

                {showPowerOffCounterSignatureRequestSection ? (
                    <CounterSignatureRequestInput
                        counterSignatureRequestInputHandler={powerOffCounterSignatureRequestHandler}
                        inputId="power-off-counter-signature-request"
                        resources={resources}
                        containerClassName="mt-3"
                    />
                ) : null}

                {showPowerOffSignatures ? (
                    <div className="d-flex mt-3">
                        <SignatureDisplay
                            resources={resources}
                            signatureLabel={resources.get("ResponsibleElectrician")}
                            signatureManager={powerOffResponsibleElectricianSignatureManager}
                            containerClassName="me-3"
                        />
                        <SignatureDisplay
                            signatureLabel={resources.get("WorkLeader")}
                            resources={resources}
                            signatureManager={powerOffWorkLeaderSignatureManager}
                            containerClassName="me-3"
                        />
                        {checklistVersion === 0 ? (
                            <SignatureDisplay
                                signatureLabel={"FBVI"}
                                resources={resources}
                                signatureManager={powerOffDirectorFlammableProductSignatureManager}
                                containerClassName="me-3"
                            />
                        ) : (
                            <SignatureDisplay
                                signatureLabel="Port Officer"
                                resources={resources}
                                signatureManager={powerOffPortOfficerSignatureManager}
                                containerClassName="me-3"
                            />
                        )}

                        {showPowerOffExternalDirectorFlammableProductSignature ? (
                            <SignatureDisplay
                                signatureLabel={"FBVE"}
                                resources={resources}
                                signatureManager={powerOffExternalDirectorFlammableProductSignatureManager}
                                containerClassName="me-3"
                            />
                        ) : null}
                    </div>
                ) : null}

                <h6 className="fw-bold mt-4">{resources.get("ConnectionOfElectricFence")}</h6>
                <p>{resources.get("IsTheFollowingDone")}</p>
                {checklistVersion === 0 ? (
                    <PowerOnVersion0
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                    />
                ) : (
                    <PowerOnVersion2026_02
                        resources={resources}
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                    />
                )}

                {showPowerOnCounterSignatureRequestSection ? (
                    <CounterSignatureRequestInput
                        counterSignatureRequestInputHandler={powerOnCounterSignatureRequestHandler}
                        inputId="power-on-counter-signature-request"
                        resources={resources}
                        containerClassName="mt-3"
                    />
                ) : null}

                {showPowerOnSignatures ? (
                    <div className="d-flex mt-3">
                        <SignatureDisplay
                            resources={resources}
                            signatureLabel={resources.get("ResponsibleElectrician")}
                            signatureManager={powerOnResponsibleElectricianSignatureManager}
                            containerClassName="me-3"
                        />
                        <SignatureDisplay
                            resources={resources}
                            signatureLabel={resources.get("WorkLeader")}
                            signatureManager={powerOnWorkLeaderSignatureManager}
                            containerClassName="me-3"
                        />
                        {checklistVersion === 0 ? (
                            <SignatureDisplay
                                signatureManager={powerOnDirectorFlammableProductSignatureManager}
                                resources={resources}
                                signatureLabel={"FBVI"}
                                containerClassName="me-3"
                            />
                        ) : (
                            <SignatureDisplay
                                signatureLabel="Port Officer"
                                resources={resources}
                                signatureManager={powerOnPortOfficerSignatureManager}
                                containerClassName="me-3"
                            />
                        )}
                        {showPowerOnExternalDirectorFlammableProductSignature ? (
                            <SignatureDisplay
                                resources={resources}
                                signatureLabel="FBVE"
                                signatureManager={powerOnExternalDirectorFlammableProductSignatureManager}
                                containerClassName="me-3"
                            />
                        ) : null}
                    </div>
                ) : null}
            </Modal.Body>
        </Modal>
    );
}

export default ElectricFenceChecklistModal;

type ChecklistProps = {
    resources: Resources;
    formState: ElectricFenceChecklistFormState;
    changeHandler: (key: keyof ElectricFenceChecklistFormState, value: any) => void;
    canManageContent: boolean;
};

function PowerOffVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="port-entry-is-informed-of-work"
                    labelText={resources.get("IsPortEntryInformedOfWork")}
                    onChange={(updatedValue) => changeHandler("portEntryIsInformedOfWork", updatedValue)}
                    resources={resources}
                    value={formState.portEntryIsInformedOfWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="low-high-voltage-is-cut"
                    labelText={resources.get("IsLowHighVoltageCut")}
                    onChange={(updatedValue) => changeHandler("lowHighVoltageIsCut", updatedValue)}
                    resources={resources}
                    value={formState.lowHighVoltageIsCut.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="high-voltage-is-grounded"
                    labelText={resources.get("IsHighVoltageGrounded")}
                    onChange={(updatedValue) => changeHandler("highVoltageIsGrounded", updatedValue)}
                    resources={resources}
                    value={formState.highVoltageIsGrounded.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-work-in-progress-sign-up"
                    labelText={resources.get("IsWorkInProgressSignUp")}
                    onChange={(updatedValue) => changeHandler("workInProgressSignIsUp", updatedValue)}
                    resources={resources}
                    value={formState.workInProgressSignIsUp.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="power-off-is-controlled"
                    labelText={resources.get("ControlledPowerOffBeforeWork")}
                    onChange={(updatedValue) => changeHandler("powerOffIsControlled", updatedValue)}
                    resources={resources}
                    value={formState.powerOffIsControlled.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="security-coordinator-informed-permit-updated"
                    labelText={resources.get("SecurityCoordinatorInformedPermitUpdated")}
                    onChange={(updatedValue) => changeHandler("securityCoordinatorInformedPermitUpdated", updatedValue)}
                    resources={resources}
                    value={formState.securityCoordinatorInformedPermitUpdated.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function PowerOffVersion2026_02({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="port-entry-is-informed-of-work"
                    labelText={resources.get("IsPortEntryInformedOfWork")}
                    onChange={(updatedValue) => changeHandler("portEntryIsInformedOfWork", updatedValue)}
                    resources={resources}
                    value={formState.portEntryIsInformedOfWork.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="low-high-voltage-is-cut"
                    labelText={resources.get("IsLowHighVoltageCut")}
                    onChange={(updatedValue) => changeHandler("lowHighVoltageIsCut", updatedValue)}
                    resources={resources}
                    value={formState.lowHighVoltageIsCut.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="high-voltage-is-grounded"
                    labelText={resources.get("IsHighVoltageGrounded")}
                    onChange={(updatedValue) => changeHandler("highVoltageIsGrounded", updatedValue)}
                    resources={resources}
                    value={formState.highVoltageIsGrounded.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="sign-is-at-breaker-or-fuse"
                    labelText={resources.get("SignIsAtBreakerOrFuse")}
                    onChange={(updatedValue) => changeHandler("signIsAtBreakerOrFuse", updatedValue)}
                    resources={resources}
                    value={formState.signIsAtBreakerOrFuse.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="power-off-will-be-controlled"
                    labelText={resources.get("PowerOffWillBeControlled")}
                    onChange={(updatedValue) => changeHandler("powerOffWillBeControlled", updatedValue)}
                    resources={resources}
                    value={formState.powerOffWillBeControlled.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="security-coordinator-informed-about-started-work"
                    labelText={resources.get("SecurityCoordinatorInformedAboutStartedWork")}
                    onChange={(updatedValue) =>
                        changeHandler("securityCoordinatorInformedAboutStartedWork", updatedValue)
                    }
                    resources={resources}
                    value={formState.securityCoordinatorInformedAboutStartedWork.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function PowerOnVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="controlled-no-person-at-fence"
                    labelText={resources.get("ControlledNoPersonAtFence")}
                    onChange={(updatedValue) => changeHandler("controlledNoPersonAtFence", updatedValue)}
                    resources={resources}
                    value={formState.controlledNoPersonAtFence.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="security-round-performed"
                    labelText={resources.get("SecurityRoundPerformed")}
                    onChange={(updatedValue) => changeHandler("securityRoundPerformed", updatedValue)}
                    resources={resources}
                    value={formState.securityRoundPerformed.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="checked-power-is-on"
                    labelText={resources.get("CheckedPowerIsOn")}
                    onChange={(updatedValue) => changeHandler("checkedPowerIsOn", updatedValue)}
                    resources={resources}
                    value={formState.checkedPowerIsOn.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="security-coordinator-informed-permit-returned"
                    labelText={resources.get("SecurityCoordinatorInformedPermitReturned")}
                    onChange={(updatedValue) =>
                        changeHandler("securityCoordinatorInformedPermitReturned", updatedValue)
                    }
                    resources={resources}
                    value={formState.securityCoordinatorInformedPermitReturned.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function PowerOnVersion2026_02({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <>
            <ul className="list-unstyled">
                <li className="m-3">
                    <ChecklistRadioOptions
                        id="controlled-no-person-at-fence"
                        labelText={resources.get("ControlledNoPersonAtFence")}
                        onChange={(updatedValue) => changeHandler("controlledNoPersonAtFence", updatedValue)}
                        resources={resources}
                        value={formState.controlledNoPersonAtFence.value}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-3">
                    <ChecklistRadioOptions
                        id="security-round-performed"
                        labelText={resources.get("SecurityRoundPerformed")}
                        onChange={(updatedValue) => changeHandler("securityRoundPerformed", updatedValue)}
                        resources={resources}
                        value={formState.securityRoundPerformed.value}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-3">
                    <ChecklistRadioOptions
                        id="checked-power-is-on"
                        labelText={resources.get("CheckedPowerIsOn")}
                        onChange={(updatedValue) => changeHandler("checkedPowerIsOn", updatedValue)}
                        resources={resources}
                        value={formState.checkedPowerIsOn.value}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-3">
                    <ChecklistRadioOptions
                        id="electrician-informed-port-entry-work-complete"
                        labelText={resources.get("ElectricianInformedPortEntryWorkComplete")}
                        onChange={(updatedValue) =>
                            changeHandler("electricianInformedPortEntryWorkComplete", updatedValue)
                        }
                        resources={resources}
                        value={formState.electricianInformedPortEntryWorkComplete?.value}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-3">
                    <ChecklistRadioOptions
                        id="security-coordinator-informed-work-complete"
                        labelText={resources.get("SecurityCoordinatorInformedWorkComplete")}
                        onChange={(updatedValue) =>
                            changeHandler("securityCoordinatorInformedWorkComplete", updatedValue)
                        }
                        resources={resources}
                        value={formState.securityCoordinatorInformedWorkComplete?.value}
                        readOnly={!canManageContent}
                    />
                </li>
            </ul>
        </>
    );
}
